import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';

interface SummitForm {
  name: string;
  latitude: string;
  longitude: string;
  email: string;
  details: string;
}

const SummitSubmissionContent: React.FC = () => {
  const { t, language } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<SummitForm>({
    name: '',
    latitude: '',
    longitude: '',
    email: '',
    details: ''
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateEmail(formData.email)) {
      setError(t('summit_submission.error.invalidEmail') as string);
      return;
    }
    
    try {
      const { error: supabaseError } = await supabase
        .from('summit_submission')
        .insert([{
          name: formData.name,
          latitude: parseFloat(formData.latitude.replace(',', '.')),
          longitude: parseFloat(formData.longitude.replace(',', '.')),
          email: formData.email,
          details: formData.details,
          source: 'website'
        }]);

      if (supabaseError) throw supabaseError;
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting summit:', err);
      setError(t('summit_submission.error.submission') as string);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <main>
      <h1>{t('summit_submission.title')}</h1>
      <p className="description">{t('summit_submission.description')}</p>

      {submitted ? (
        <div className="success-content">
          <h3>{t('summit_submission.success.title')}</h3>
          <p>{t('summit_submission.success.message')}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="summit-form">
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">{t('summit_submission.form.name.label')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('summit_submission.form.name.placeholder') as string}
              required
            />
          </div>

          <div className="form-group coordinates">
            <h4>{t('summit_submission.form.coordinates.title')}</h4>
            <div className="coordinates-inputs">
              <div>
                <label htmlFor="latitude">{t('summit_submission.form.coordinates.latitude.label')}</label>
                <input
                  type="number"
                  id="latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  placeholder={t('summit_submission.form.coordinates.latitude.placeholder') as string}
                  step="any"
                  required
                />
              </div>
              <div>
                <label htmlFor="longitude">{t('summit_submission.form.coordinates.longitude.label')}</label>
                <input
                  type="number"
                  id="longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  placeholder={t('summit_submission.form.coordinates.longitude.placeholder') as string}
                  step="any"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('summit_submission.form.email.label')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('summit_submission.form.email.placeholder') as string}
              pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
              title={t('summit_submission.form.email.validation') as string}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="details">{t('summit_submission.form.details.label')}</label>
            <textarea
              id="details"
              name="details"
              value={formData.details}
              onChange={handleChange}
              placeholder={t('summit_submission.form.details.placeholder') as string}
              rows={5}
            />
          </div>

          <button type="submit" className="submit-button">
            {t('summit_submission.form.submit')}
          </button>
        </form>
      )}
    </main>
  );
};

export default SummitSubmissionContent; 